import type { Map, GeoJSONSource, GeoJSONSourceRaw, MapMouseEvent, MapboxGeoJSONFeature } from "mapbox-gl";
import { LngLat } from "mapbox-gl";
import type { ISearchGeofence, SearchGeofenceCustomCTA } from "Services/API/API";
import tippy from "tippy.js";
import circleToPolygon from "circle-to-polygon";

type IGeoFenceProperties = {
   IGeoFenceProperties: true,
   customCTAUrl?: SearchGeofenceCustomCTA["url"],
   customCTAText?: SearchGeofenceCustomCTA["text"],
} & Pick<ISearchGeofence, "radiusMeters" | "venueId" | "latitude" | "longitude" | "message">

const GEOFENCE_SOURCE_NAME = "geofencing";
const GEOFENCE_LAYER_NAME = "geofencing";

const createGeoJSONCircleFeature = function (geoFence: ISearchGeofence, numberOfEdges?: number): GeoJSON.Feature {
   if (!numberOfEdges) numberOfEdges = 64;

   const properties: IGeoFenceProperties = {
      IGeoFenceProperties: true,
      radiusMeters: geoFence.radiusMeters,
      latitude: geoFence.latitude,
      longitude: geoFence.longitude,
      message: geoFence.message,
      venueId: geoFence.venueId
   }

   if (geoFence.customCTA) {
      properties.customCTAUrl = geoFence.customCTA.url;
      properties.customCTAText = geoFence.customCTA.text;
   }

   return {
      type: "Feature",
      geometry: circleToPolygon([geoFence.longitude, geoFence.latitude], geoFence.radiusMeters, numberOfEdges),
      properties
   };
}

export function setGeofencesData(map: Map, geofences: ISearchGeofence[]): void {
   (map.getSource(GEOFENCE_SOURCE_NAME) as GeoJSONSource)?.setData({
      type: "FeatureCollection",
      features: geofences.map(x => createGeoJSONCircleFeature(x))
   })
}

export function initGeofencesLayer(map: Map, geofences: ISearchGeofence[]): void {
   const initGeoJSON: GeoJSONSourceRaw = {
      type: "geojson",
      data: {
         type: "FeatureCollection",
         features: geofences.map(x => createGeoJSONCircleFeature(x))
      }
   };
   map.addSource(GEOFENCE_SOURCE_NAME, initGeoJSON);
   map.addLayer({
      "id": GEOFENCE_LAYER_NAME,
      "type": "fill",
      "source": GEOFENCE_SOURCE_NAME,
      "layout": {},
      "paint": {
         "fill-antialias": true,
         "fill-color": "rgba(255, 141, 107, 0.20)",
         "fill-opacity": 1,
         "fill-outline-color": "#FF8D6B",
      }
   });

   function getProperties(event: MapMouseEvent & { features?: MapboxGeoJSONFeature[] | undefined; }) {
      return event.features
         ?.filter(f => f.properties && f.properties.IGeoFenceProperties)
         .map(x => x.properties as IGeoFenceProperties)
         .sort((a, b) => a.radiusMeters - b.radiusMeters)[0];
   }

   const canvasContainer = map.getCanvasContainer();
   const popupContentElement = document.createElement("div");
   popupContentElement.className = "hq_popup";
   popupContentElement.id = "hq_popup";

   const popupAnchor = document.createElement("div");
   popupAnchor.className = "hq_popup-anchor";
   canvasContainer.append(popupAnchor);

   const tippyInstance = tippy(popupAnchor, {
      arrow: true,
      interactiveBorder: 0,
      interactive: true,
      maxWidth: "none",
      content: popupContentElement,
      offset: [0, 0],
      placement: "top",
      appendTo: document.body,
      theme: "liquidspace"
   });

   let venueId: Guid | undefined;
   map.on("mousemove", GEOFENCE_LAYER_NAME, e => {
      const properties = getProperties(e);
      if (properties) {
         if (properties.venueId != venueId) {
            popupContentElement.innerHTML = "";

            popupContentElement.append(properties.message);

            if (properties.customCTAUrl && properties.customCTAText) {
               const ctaElement = document.createElement("a");
               ctaElement.className = "hq_popup-CTA";
               ctaElement.href = properties.customCTAUrl;
               ctaElement.innerText = properties.customCTAText;
               ctaElement.target = "_blank";

               popupContentElement.append(ctaElement);
            }


            tippyInstance.setContent(popupContentElement);
            venueId = properties.venueId;
         }

         const featureCenter = new LngLat(properties.longitude, properties.latitude).wrap();

         const featureBounds = featureCenter.toBounds(properties.radiusMeters);
         const f_ne = map.project(featureBounds.getNorthEast());
         const f_sw = map.project(featureBounds.getSouthWest());

         const mapBounds = map.getBounds();
         const m_ne = map.project(mapBounds.getNorthEast());
         const m_sw = map.project(mapBounds.getSouthWest());

         const x = Math.max(f_sw.x, m_sw.x);
         const width = Math.min(f_ne.x, m_ne.x) - x;

         const y = Math.max(f_ne.y, m_ne.y);
         const height = Math.min(f_sw.y, m_sw.y) - y;

         popupAnchor.style.top = `${y}px`;
         popupAnchor.style.left = `${x}px`;
         popupAnchor.style.height = `${height}px`;
         popupAnchor.style.width = `${width}px`;

         tippyInstance.popperInstance?.update();
      }
   });
   // map.on("mouseleave", GEOFENCE_LAYER_NAME, () => {
   //    popupAnchor.style.top = "0";
   //    popupAnchor.style.left = "0";
   //    popupAnchor.style.height = "0";
   //    popupAnchor.style.width = "0";

   //    tippyInstance.hide();
   // });
}